'use client';

import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

type QueryKey = readonly unknown[];

/**
 * Determines if a mutation key matches a filter key using React Query's matching logic
 *
 * @param mutationKey - The mutation key to check
 * @param filterKey - The filter key to match against
 * @returns True if the mutation key matches the filter criteria
 */
const matchMutationKey = (mutationKey: unknown, filterKey: QueryKey): boolean => {
  if (filterKey.length === 0) {
    return true;
  }

  if (!Array.isArray(mutationKey)) {
    return false;
  }

  return filterKey.every((filterValue, index) => {
    const mutationValue = mutationKey[index];

    if (filterValue === undefined) {
      return true;
    }

    if (typeof filterValue === 'function') {
      return filterValue(mutationValue);
    }

    return filterValue === mutationValue;
  });
};

/**
 * Hook that tracks the loading state of mutations matching a specific query key pattern
 *
 * This hook monitors the React Query mutation cache and updates its state
 * whenever matching mutations change their loading state.
 *
 * @param queryKey - The query key pattern to match mutations against
 * @returns A boolean indicating if any matching mutation is currently loading
 *
 * @example
 * // Track all cart mutations
 * const isCartLoading = useMutationLoading('cart');
 *
 * // Track only addToCart operations
 * const isAddToCartLoading = useMutationLoading(['cart', 'addToCart']);
 *
 * // Track updateCartItem operations for any item (using wildcard)
 * const isUpdateCartLoading = useMutationLoading(['cart', 'updateCartItem', undefined]);
 */
export const useMutationLoading = (queryKey: QueryKey): boolean => {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const mutationCache = queryClient.getMutationCache();

  const queryKeyString = JSON.stringify(queryKey);

  useEffect(() => {
    const parsedQueryKey = JSON.parse(queryKeyString) as QueryKey;

    const checkActiveMutations = () => {
      const mutations = mutationCache.getAll();

      const matchingMutations = mutations.filter((mutation) => {
        const mutationKey = mutation.options.mutationKey;
        return mutationKey && matchMutationKey(mutationKey, parsedQueryKey);
      });

      const hasActiveMutation = matchingMutations.some((mutation) => mutation.state.status === 'pending');

      setIsLoading(hasActiveMutation);
    };
    checkActiveMutations();

    const unsubscribe = mutationCache.subscribe((event) => {
      const mutationKey = event.mutation?.options.mutationKey;
      const isMatchingMutation = mutationKey && matchMutationKey(mutationKey, parsedQueryKey);

      if (isMatchingMutation || event.type === 'added' || event.type === 'removed') {
        checkActiveMutations();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [mutationCache, queryKeyString]);

  return isLoading;
};
